import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import { successToast, errorToast } from '../../lib/helpers';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import clsx from 'clsx';

function CustomerContactsGrouped() {
  const { uinfo } = useContext(GlobalContext);
  const [messages, setMessages] = useState([]);
  const [groupedMessages, setGroupedMessages] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());
  const [newMessage, setNewMessage] = useState({});
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get customers for account name lookup
  const { data: customersData } = useApi({
    url: '/visiondashboard/customers/profiles',
    initial: []
  });

  // Fetch all messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const response = await haxios.get('/visiondashboard/messages');
        const messagesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setMessages(messagesData);
      } catch (error) {
        console.error('Error fetching messages:', error);
        errorToast('Failed to load messages');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // Group messages by account
  useEffect(() => {
    const grouped = {};
    messages.forEach(msg => {
      const accountId = msg.Acct || msg.account || msg.acct;
      if (!accountId) return;

      if (!grouped[accountId]) {
        grouped[accountId] = {
          account: accountId,
          messages: [],
          lastMessageDate: null,
          messageCount: 0
        };
      }

      grouped[accountId].messages.push(msg);
      const msgDate = msg.timestamp ? new Date(msg.timestamp) : null;
      if (msgDate && (!grouped[accountId].lastMessageDate || msgDate > grouped[accountId].lastMessageDate)) {
        grouped[accountId].lastMessageDate = msgDate;
      }
    });

    // Sort messages within each group by date (newest first)
    Object.values(grouped).forEach(group => {
      group.messages.sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        return dateB - dateA;
      });
      group.messageCount = group.messages.length;
    });

    setGroupedMessages(grouped);
  }, [messages]);

  const getCustomerName = (accountId) => {
    const customer = customersData?.find(c => 
      c.KeyCode === accountId || 
      c.Acct === accountId ||
      c.account === accountId
    );
    return customer?.Name || customer?.name || customer?.CustomerName || accountId;
  };

  const toggleAccount = (accountId) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const handleSendMessage = async (accountId) => {
    const messageText = newMessage[accountId]?.trim();
    if (!messageText) {
      errorToast('Please enter a message');
      return;
    }

    try {
      setSending(true);
      await haxios.post('/visiondashboard/messages/add', {
        acct: accountId,
        message: messageText,
        op: uinfo?.Inits || uinfo?.Name,
        timestamp: new Date().toISOString()
      });

      successToast('Message sent successfully');
      setNewMessage(prev => ({ ...prev, [accountId]: '' }));
      
      // Refresh messages
      const response = await haxios.get('/visiondashboard/messages');
      const messagesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setMessages(messagesData);
    } catch (error) {
      console.error('Error sending message:', error);
      errorToast('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sortedAccounts = Object.values(groupedMessages).sort((a, b) => {
    if (!a.lastMessageDate && !b.lastMessageDate) return 0;
    if (!a.lastMessageDate) return 1;
    if (!b.lastMessageDate) return -1;
    return b.lastMessageDate - a.lastMessageDate;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500">Loading customer contacts...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Customer Contacts (Grouped by Account)</h2>
        <p className="text-sm text-gray-600 mt-1">
          {sortedAccounts.length} customers with {messages.length} total messages
        </p>
      </div>

      <div className="space-y-2">
        {sortedAccounts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No customer contacts found.
          </div>
        ) : (
          sortedAccounts.map((group) => {
            const isExpanded = expandedAccounts.has(group.account);
            const customerName = getCustomerName(group.account);

            return (
              <div key={group.account} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Account Header */}
                <div
                  className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleAccount(group.account)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                      )}
                      <div>
                        <div className="font-semibold text-gray-900">{customerName}</div>
                        <div className="text-sm text-gray-500">{group.account}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-600">
                        {group.messageCount} message{group.messageCount !== 1 ? 's' : ''}
                      </div>
                      {group.lastMessageDate && (
                        <div className="text-sm text-gray-500">
                          {format(group.lastMessageDate, 'dd/MM/yyyy HH:mm')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Messages */}
                {isExpanded && (
                  <div className="bg-white">
                    {/* Messages List */}
                    <div className="max-h-64 overflow-y-auto">
                      {group.messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={clsx(
                            'px-4 py-3 border-b border-gray-100',
                            idx === 0 && 'bg-blue-50'
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {msg.staff_member || msg.Op || 'Unknown'}
                                </span>
                                {msg.timestamp && (
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(msg.timestamp), 'dd/MM/yyyy HH:mm')}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {msg.message || msg.note || 'No message content'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add New Message */}
                    <div className="px-4 py-3 bg-gray-50 border-t">
                      <div className="flex items-center space-x-2">
                        <textarea
                          value={newMessage[group.account] || ''}
                          onChange={(e) => setNewMessage(prev => ({
                            ...prev,
                            [group.account]: e.target.value
                          }))}
                          placeholder="Type a message..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          rows="2"
                        />
                        <button
                          onClick={() => handleSendMessage(group.account)}
                          disabled={sending || !newMessage[group.account]?.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                          <span>Send</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default CustomerContactsGrouped;

